import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { EventManagementIcon, DeleteIcon, PlusIcon, ResetIcon } from '../Icons'
import { CardContent, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { cn } from '@/lib/utils'
import {
  handleIntegerKeyDown,
  handlePriceInput,
  handlePriceKeyDown,
  handlePriceClick,
  formatPriceWithCommas,
} from '@/utils/formFieldUtils'
import { type EventFormDataType } from '@/schemas/eventForm.schema'
import { HEROFLIGHT_DURATION } from '@/constants/event'
import { useState, useImperativeHandle, type RefObject } from 'react'
import { Button } from '../ui/button'
import CustomRichTextEditor from '../CustomRichTextEditor'
import { MultiSelect } from '../Multiselect'
import { AD_SLOT_OPTIONS, AdvertisementPlacements } from '@/constants/sponsor'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import type { SubEventFormDataType } from '@/schemas/subEventForm.schema'
import { formatDateDisplay } from '@/utils/dateUtils'
import { MAX_PACKAGES_LIMIT } from '@/constants'

export interface EventPackageFormRef {
  hasAnyPackageErrors: () => boolean
  getHeroDurationErrors: () => { [key: number]: string }
}

interface EventPackageFormProps {
  isSubEvent?: boolean
  startDate?: Date
  endDate?: Date
  ref: RefObject<EventPackageFormRef | null>
}

export default function EventPackageForm({
  ref,
  isSubEvent,
  startDate,
  endDate,
}: Readonly<EventPackageFormProps>) {
  const { control, watch, setValue, clearErrors } = useFormContext<
    EventFormDataType | SubEventFormDataType
  >()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sponsorshipPackages',
  })

  const [heroDurationErrors, setHeroDurationErrors] = useState<{
    [key: number]: string
  }>({})

  useImperativeHandle(ref, () => ({
    hasAnyPackageErrors: () => {
      let hasError = false
      const sponsorShipPackages = watch('sponsorshipPackages')
      sponsorShipPackages.forEach((_, ind) => {
        const priorityError = getPriorityError(ind)
        if (priorityError) {
          hasError = true
        }
      })
      return hasError
    },
    getHeroDurationErrors: () => heroDurationErrors,
  }))

  const handleAddPackage = () => {
    if (fields.length >= MAX_PACKAGES_LIMIT) return

    append({
      id: Date.now().toString(),
      name: '',
      description: '',
      price: '',
      availablePlacements: [],
      priority: '',
      heroDuration: '',
      maxSponsorshipsLimit: '',
      isNew: true,
    })
  }

  const clearPackageErrors = (index: number) => {
    clearErrors([
      `sponsorshipPackages.${index}.name`,
      `sponsorshipPackages.${index}.description`,
      `sponsorshipPackages.${index}.price`,
      `sponsorshipPackages.${index}.availablePlacements`,
      `sponsorshipPackages.${index}.priority`,
      `sponsorshipPackages.${index}.heroDuration`,
      `sponsorshipPackages.${index}.maxSponsorshipsLimit`,
    ])
    setHeroDurationErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[index]
      return newErrors
    })
  }

  const handleRemovePackage = (index: number) => {
    if (fields.length <= 1) return
    clearPackageErrors(index)
    remove(index)
  }

  const handlePackageReset = (index: number) => {
    setValue(`sponsorshipPackages.${index}.name`, '')
    setValue(`sponsorshipPackages.${index}.description`, '')
    setValue(`sponsorshipPackages.${index}.price`, '')
    setValue(`sponsorshipPackages.${index}.availablePlacements`, [])
    setValue(`sponsorshipPackages.${index}.priority`, '')
    setValue(`sponsorshipPackages.${index}.heroDuration`, '')
    setValue(`sponsorshipPackages.${index}.maxSponsorshipsLimit`, '')
    clearPackageErrors(index)
  }

  const getPriorityError = (index: number): string => {
    const currentPriority = watch(`sponsorshipPackages.${index}.priority`)
    if (!currentPriority) return ''

    const hasDuplicate = fields.some((_, i) => {
      if (i === index) return false
      const otherPriority = watch(`sponsorshipPackages.${i}.priority`)
      return otherPriority === currentPriority
    })

    if (hasDuplicate) {
      return 'Two packages cannot have the same priority'
    }

    return ''
  }

  const showEventDates = isSubEvent && startDate && endDate

  return (
    <>
      <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
        <EventManagementIcon className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-semibold text-ss-neutral-950">
          Sponsor Packages
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {showEventDates && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-ss-neutral-25 rounded-lg border border-ss-neutral-100">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-ss-neutral-600">
                Main Event Start Date
              </Label>
              <Input
                value={startDate ? formatDateDisplay(startDate) : 'N/A'}
                disabled
                className="h-11 bg-ss-neutral-50 border-ss-neutral-100 text-ss-neutral-600 cursor-not-allowed"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-ss-neutral-600">
                Main Event End Date
              </Label>
              <Input
                value={endDate ? formatDateDisplay(endDate) : 'N/A'}
                disabled
                className="h-11 bg-ss-neutral-50 border-ss-neutral-100 text-ss-neutral-600 cursor-not-allowed"
              />
            </div>
          </div>
        )}

        {fields.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-ss-neutral-600 mb-4">
              No sponsorship packages added yet
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddPackage}
              className="gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add Package
            </Button>
          </div>
        ) : (
          fields.map((field, index) => {
            const pkg = watch(`sponsorshipPackages.${index}`)
            const adSlots = pkg.availablePlacements || []
            const hasHeroAds = adSlots.includes(AdvertisementPlacements.HERO)
            const priorityError = getPriorityError(index)

            return (
              <div key={field.id} className="space-y-4">
                {/* Package Header */}
                <div className="bg-ss-neutral-25 box-border flex flex-col gap-2.5 items-start justify-start px-3 py-1.5 relative rounded border-r-[#cdd1d9] w-full">
                  <div className="flex gap-8 items-start justify-start w-full">
                    <div className="flex gap-1.5 grow items-center justify-between">
                      <h3 className="font-bold text-ss-neutral-950 text-base leading-[22px]">
                        Package {index + 1}
                      </h3>
                      <div className="flex align-middle items-center gap-2">
                        {fields.length === 1 &&
                          (() => {
                            const pkg = watch('sponsorshipPackages')?.[0] || {}
                            const hasAnyValue = !!(
                              pkg.name ||
                              pkg.description ||
                              pkg.price ||
                              (pkg.availablePlacements &&
                                pkg.availablePlacements.length > 0) ||
                              pkg.priority ||
                              pkg.heroDuration ||
                              pkg.maxSponsorshipsLimit
                            )

                            const isBooked = pkg.isBooked || false

                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      disabled={!hasAnyValue || isBooked}
                                      type="button"
                                      variant="ghost"
                                      aria-label="Reset package fields"
                                      className="p-0 flex text-ss-blue-900 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-900 hover:bg-transparent"
                                      onClick={() => handlePackageReset(index)}
                                    >
                                      <ResetIcon
                                        className="size-5 hover:text-ss-blue-900"
                                        aria-hidden="true"
                                      />
                                      <span className="font-medium text-sm leading-[18px] hover:text-ss-blue-900 disabled:text-ss-neutral-200">
                                        Reset
                                      </span>
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {isBooked && (
                                  <TooltipContent>
                                    Someone has already purchased this package.
                                    Can't reset.
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )
                          })()}
                        {index === fields.length - 1 && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={handleAddPackage}
                              disabled={fields.length >= MAX_PACKAGES_LIMIT}
                              aria-label="Add more packages"
                              className="p-0 flex text-ss-blue-600 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-700 hover:bg-transparent"
                            >
                              <PlusIcon
                                className="size-5 hover:text-ss-blue-700"
                                aria-hidden="true"
                              />
                              <span className="font-medium text-sm leading-[18px] hover:text-ss-blue-700 disabled:text-ss-neutral-200">
                                Add More
                              </span>
                            </Button>
                          </>
                        )}
                        {fields.length > 1 &&
                          (() => {
                            const sponsorShip =
                              watch('sponsorshipPackages')?.[index] || {}
                            const isBooked = sponsorShip.isBooked || false
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      disabled={isBooked}
                                      type="button"
                                      variant="ghost"
                                      onClick={() => handleRemovePackage(index)}
                                      className="gap-1 flex p-0 h-auto"
                                      aria-label={`Delete Package ${index + 1}`}
                                    >
                                      <DeleteIcon
                                        strokeWidth={1.4}
                                        className="h-5 w-5 text-ss-critical-200"
                                        aria-hidden="true"
                                      />
                                      <span className="font-medium text-ss-critical-200 text-sm leading-[18px]">
                                        Delete
                                      </span>
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {isBooked && (
                                  <TooltipContent>
                                    Someone has already purchased this package.
                                    Can't delete.
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )
                          })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Form Fields */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Package Name */}
                    <div className="space-y-2">
                      <Label
                        htmlFor={`package-name-${index}`}
                        className="text-sm font-medium text-ss-neutral-950"
                        variant="required"
                      >
                        Package Name
                      </Label>
                      <Controller
                        name={`sponsorshipPackages.${index}.name`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              id={`package-name-${index}`}
                              placeholder="Enter package name"
                              className={cn(
                                'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                error && 'border-ss-critical-200'
                              )}
                              aria-invalid={!!error}
                            />
                            {error && (
                              <p className="text-xs text-ss-critical-200 mt-1.5">
                                {error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>

                    {/* Priority */}
                    <div className="space-y-2">
                      <Label
                        htmlFor={`package-price-${index}`}
                        className="text-sm font-medium text-ss-neutral-950"
                        variant="required"
                      >
                        Package Price ($)
                      </Label>
                      <Controller
                        name={`sponsorshipPackages.${index}.price`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              value={formatPriceWithCommas(
                                field.value?.toString() || ''
                              )}
                              onChange={e => {
                                handlePriceInput(e, field.onChange)
                                if (!e.target.value) {
                                  clearErrors(
                                    `sponsorshipPackages.${index}.heroDuration`
                                  )
                                  setHeroDurationErrors(prev => {
                                    const newErrors = { ...prev }
                                    delete newErrors[index]
                                    return newErrors
                                  })
                                }
                              }}
                              onKeyDown={handlePriceKeyDown}
                              onClick={handlePriceClick}
                              onFocus={handlePriceClick}
                              id={`package-price-${index}`}
                              type="text"
                              inputMode="decimal"
                              placeholder="Enter package price"
                              min={0}
                              max={99999999.99}
                              className={cn(
                                'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                error && 'border-ss-critical-200'
                              )}
                              aria-invalid={!!error}
                            />
                            {error && (
                              <p className="text-xs text-ss-critical-200 mt-1.5">
                                {error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label
                      htmlFor={`package-description-${index}`}
                      className="text-sm font-medium text-ss-neutral-950"
                      variant="required"
                    >
                      Description
                    </Label>
                    <Controller
                      name={`sponsorshipPackages.${index}.description`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <CustomRichTextEditor
                            toolbarFeatures={['bulletList']}
                            value={field.value ?? ''}
                            onChange={value =>
                              field.onChange(value === '<p></p>' ? '' : value)
                            }
                            height={'85px'}
                            placeholder="Enter package description"
                            aria-invalid={!!error}
                            className={cn(
                              'border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                              error && 'border-ss-critical-200'
                            )}
                          />
                          {error && (
                            <p className="text-xs text-ss-critical-200 mt-1.5">
                              {error.message}
                            </p>
                          )}
                        </>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Package Price */}
                    <div className="space-y-2">
                      <Label
                        htmlFor={`package-priority-${index}`}
                        className="text-sm font-medium text-ss-neutral-950"
                        variant="required"
                      >
                        Priority
                      </Label>
                      <Controller
                        name={`sponsorshipPackages.${index}.priority`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              id={`package-priority-${index}`}
                              type="number"
                              min={1}
                              max={100}
                              placeholder="Enter priority"
                              onKeyDown={handleIntegerKeyDown}
                              className={cn(
                                'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                (error || priorityError) &&
                                  'border-ss-critical-200'
                              )}
                              aria-invalid={!!(error || priorityError)}
                            />
                            {(error || priorityError) && (
                              <p className="text-xs text-ss-critical-200 mt-1.5">
                                {error?.message || priorityError}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>

                    {/* Max Sponsorships Limit */}
                    <div className="space-y-2">
                      <Label
                        htmlFor={`package-max-sponsorships-${index}`}
                        className="text-sm font-medium text-ss-neutral-950"
                        variant="required"
                      >
                        Max Sponsorships Limit
                      </Label>
                      <Controller
                        name={`sponsorshipPackages.${index}.maxSponsorshipsLimit`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              value={field.value?.toString() || ''}
                              onChange={e => {
                                field.onChange(e.target.value || '')
                              }}
                              onKeyDown={handleIntegerKeyDown}
                              id={`package-max-sponsorships-${index}`}
                              type="number"
                              placeholder="Enter max sponsorships limit"
                              min={1}
                              max={100}
                              className={cn(
                                'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                error && 'border-ss-critical-200'
                              )}
                              aria-invalid={!!error}
                            />
                            {error && (
                              <p className="text-xs text-ss-critical-200 mt-1.5">
                                {error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Ad Slots */}
                    <div>
                      <Controller
                        name={`sponsorshipPackages.${index}.availablePlacements`}
                        control={control}
                        render={({ field, fieldState: { error } }) => {
                          const selectedIds =
                            field.value?.map(slot => slot) || []

                          const handleAdSlotsChange = (newIds: string[]) => {
                            field.onChange(newIds)

                            const currentHeroDuration = watch(
                              `sponsorshipPackages.${index}.heroDuration`
                            )

                            if (
                              newIds.includes(AdvertisementPlacements.HERO) &&
                              !currentHeroDuration
                            ) {
                              setValue(
                                `sponsorshipPackages.${index}.heroDuration`,
                                '7'
                              )
                            }

                            // Clear hero duration if hero ads is unchecked
                            if (
                              !newIds.includes(AdvertisementPlacements.HERO)
                            ) {
                              setValue(
                                `sponsorshipPackages.${index}.heroDuration`,
                                ''
                              )
                              clearErrors(
                                `sponsorshipPackages.${index}.heroDuration`
                              )
                              setHeroDurationErrors(prev => {
                                const newErrors = { ...prev }
                                delete newErrors[index]
                                return newErrors
                              })
                            }
                          }

                          return (
                            <>
                              <MultiSelect
                                label="Ad Slots"
                                options={AD_SLOT_OPTIONS}
                                selected={selectedIds}
                                onChange={handleAdSlotsChange}
                                placeholder="Select ad slots..."
                                required
                                className={cn(
                                  'h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full',
                                  error && 'border-ss-critical-200'
                                )}
                                labelClassName="leading-normal"
                              />
                              {error && (
                                <p className="text-xs text-ss-critical-200 mt-1.5">
                                  {error.message}
                                </p>
                              )}
                            </>
                          )
                        }}
                      />
                    </div>

                    {hasHeroAds && (
                      <div className="space-y-2">
                        <Label
                          htmlFor={`package-hero-duration-${index}`}
                          className="text-sm font-medium text-ss-neutral-950"
                        >
                          Hero Flight Duration (days)
                        </Label>
                        <Controller
                          name={`sponsorshipPackages.${index}.heroDuration`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error: zodError },
                          }) => {
                            const error =
                              heroDurationErrors[index] || zodError?.message

                            return (
                              <>
                                <Input
                                  {...field}
                                  value={field.value?.toString() || ''}
                                  onChange={e => {
                                    const value = e.target.value || ''
                                    field.onChange(value)
                                  }}
                                  onKeyDown={handleIntegerKeyDown}
                                  id={`package-hero-duration-${index}`}
                                  type="number"
                                  placeholder="Enter duration in days"
                                  min={HEROFLIGHT_DURATION.MIN}
                                  disabled={!hasHeroAds}
                                  className={cn(
                                    'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                    error && 'border-ss-critical-200',
                                    !hasHeroAds &&
                                      'bg-ss-neutral-50 cursor-not-allowed'
                                  )}
                                  aria-invalid={!!error}
                                />
                                {error && (
                                  <p className="text-xs text-ss-critical-200 mt-1.5">
                                    {error}
                                  </p>
                                )}
                              </>
                            )
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </CardContent>
    </>
  )
}
